import type { PropHomeVO, PropStageItemVO } from "@/api/v4/proptrading";

/**
 * 接口字段偶发为 number 等类型；`value?.trim()` 在 value 非 string 时会抛错（如 ref1.trim is not a function）。
 */
export function safeTrimString(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string") return v.trim();
  return String(v).trim();
}

/** 首页挑战卡从左到右固定顺序（与 StageEnum 一致） */
export const PROP_HOME_STAGE_ORDER = ["ROOKIE", "ELITE", "MASTER", "LIVE"] as const;

/**
 * 将接口返回的 stages 按 ROOKIE → ELITE → MASTER → LIVE 合并；缺失档位用占位项（不可加入、无原因）。
 */
export function mergePropStagesInOrder(apiStages: PropStageItemVO[] | undefined | null): PropStageItemVO[] {
  const map = new Map<string, PropStageItemVO>();
  (apiStages || []).forEach((s) => {
    const k = String(s.stage ?? "").trim().toUpperCase();
    if (!k) return;
    map.set(k, { ...s, stage: k });
  });
  return PROP_HOME_STAGE_ORDER.map((key) => {
    const existing = map.get(key);
    if (existing) return existing;
    return { stage: key, canJoin: false };
  });
}

/**
 * 是否允许从首页「开始挑战」进入 Prop 交易页。
 * 规则（与 /prop/home 一致）：userStage、userStatus 均无有效值 → 视为未参与过 / 未购门票，需先在滚动卡片选场次购票；
 * 任一有值（含挑战中、待复活、失败后仍带状态等）→ 可进交易页，由交易页展示对应状态。
 * 注意：不把 remainingSeconds / reviveCountLeft 单独当作可进入条件（避免 0 误判为进行中）。
 */
export function canEnterPropTradePage(home: PropHomeVO | null | undefined): boolean {
  if (!home) return false;
  const hasStage = home.userStage != null && String(home.userStage).trim() !== "";
  const hasStatus = home.userStatus != null && String(home.userStatus).trim() !== "";
  return hasStage || hasStatus;
}

const STAGE_TO_ARENA_I18N: Record<string, string> = {
  ROOKIE: "proptrading.ranking.arenaNewbie",
  ELITE: "proptrading.ranking.arenaElite",
  MASTER: "proptrading.ranking.arenaMaster",
  LIVE: "proptrading.ranking.arenaDivine",
};

export function propArenaLabelI18nKey(stage: string | null | undefined): string {
  if (!stage) return "proptrading.trade.arenaNewbie";
  const key = String(stage).toUpperCase();
  return STAGE_TO_ARENA_I18N[key] || "proptrading.trade.arenaNewbie";
}

export function formatPropRemainingSeconds(sec: number | null | undefined): string {
  if (sec == null || sec < 0 || Number.isNaN(sec)) return "--";
  const n = Math.floor(sec);
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  const s = n % 60;
  const pad = (x: number) => String(x).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

/** 当前用户场次是否为实盘 LIVE（与 /prop/home userStage 一致） */
export function isPropLiveStage(userStage: string | null | undefined): boolean {
  return String(userStage ?? "").trim().toUpperCase() === "LIVE";
}

/** 模拟盘界面展示 USDX，实盘 LIVE 下界面展示 USDT */
export function propQuoteDisplayUnit(isLiveStage: boolean): "USDT" | "USDX" {
  return isLiveStage ? "USDT" : "USDX";
}
